import {
    ADJUSTER_INITIAL_SPEC_ROWS,
    CONTROLLER_INITIAL_SPEC_ROWS,
    toSpecTitleByType,
} from '../constants/specTitles';
import {
    isWiringDiagramSpecTitle,
    normalizeWiringDiagramSpecTitle,
    WIRING_DIAGRAM_SPEC_TITLE,
} from './specFiles';

export const PRODUCT_TYPES = ['adjuster', 'controller'];

/** 리스트 조절기/제어기 필터(단일 선택)에 맞는 제품등록 초기 타입 */
export const getProductTypeFromListTypeFilters = (filters) => {
    const adjuster = filters?.adjuster !== false;
    const controller = filters?.controller !== false;
    if (adjuster && !controller) {
        return 'adjuster';
    }
    if (controller && !adjuster) {
        return 'controller';
    }
    return 'adjuster';
};

const TRAILING_SPEC_DEFS = {
    adjuster: [
        { title: '조절기 이미지', fullWidth: true, inputType: 'file' },
        { title: '펌웨어 파일', fullWidth: true, inputType: 'file' },
        { title: WIRING_DIAGRAM_SPEC_TITLE, fullWidth: true, inputType: 'file' },
        { title: '비고', fullWidth: true, inputType: 'text' },
    ],
    controller: [
        { title: '제어기 이미지', fullWidth: true, inputType: 'file' },
        { title: '펌웨어 파일', fullWidth: true, inputType: 'file' },
        { title: WIRING_DIAGRAM_SPEC_TITLE, fullWidth: true, inputType: 'file' },
        { title: '비고', fullWidth: true, inputType: 'text' },
    ],
};

export const buildDefaultFieldsForType = (productType) => {
    const count = productType === 'controller' ? CONTROLLER_INITIAL_SPEC_ROWS : ADJUSTER_INITIAL_SPEC_ROWS;
    return Array.from({ length: count }, (_, index) => ({
        title: toSpecTitleByType(productType, index),
        inputType: 'dropdown',
    }));
};

export const buildDefaultProductRegistrationConfig = () => ({
    adjuster: { fields: buildDefaultFieldsForType('adjuster') },
    controller: { fields: buildDefaultFieldsForType('controller') },
});

export const normalizeSpecFieldInputType = (raw) => {
    const t = String(raw || 'dropdown').trim().toLowerCase();
    if (t === 'text' || t === '직접입력' || t === 'input') {
        return 'text';
    }
    if (t === 'date' || t === '날짜' || t === '날짜입력' || t === 'dateinput') {
        return 'date';
    }
    return 'dropdown';
};

const normalizeField = (raw) => {
    const title = String(raw?.title || '').trim();
    const inputType = normalizeSpecFieldInputType(raw?.inputType);
    if (!title) {
        return null;
    }
    return { title, inputType };
};

export const normalizeTypeConfig = (raw, productType) => {
    const fields = Array.isArray(raw?.fields)
        ? raw.fields.map(normalizeField).filter(Boolean)
        : [];
    if (fields.length === 0) {
        return { fields: buildDefaultFieldsForType(productType) };
    }
    return { fields };
};

export const normalizeProductRegistrationConfig = (raw) => {
    const base = buildDefaultProductRegistrationConfig();
    if (!raw || typeof raw !== 'object') {
        return base;
    }
    return {
        adjuster: normalizeTypeConfig(raw.adjuster, 'adjuster'),
        controller: normalizeTypeConfig(raw.controller, 'controller'),
    };
};

export const buildSpecTemplatesFromConfig = (productType, config) => {
    const normalized = normalizeProductRegistrationConfig(config);
    const typeKey = productType === 'controller' ? 'controller' : 'adjuster';
    const normalRows = normalized[typeKey].fields.map((field) => ({
        title: field.title,
        inputType: field.inputType,
        customTitle: false,
        fullWidth: false,
    }));
    const trailing = (TRAILING_SPEC_DEFS[typeKey] || []).map((item) => ({ ...item }));
    return [...normalRows, ...trailing];
};

/** 제품등록 설정 기본 사양(이미지·파일·비고·사용자 추가 제목 제외) */
export const isDefaultRegistrationSpec = (spec) => (
    !!spec && !spec.fullWidth && !spec.customTitle
);

export const isSpecHidden = (spec) => !!spec?.hidden;

/** 리스트 세부사항 등 열람 시 숨김 처리된 사양 제외 */
export const getVisibleSpecs = (specs, { includeHidden = false } = {}) => {
    const list = Array.isArray(specs) ? specs : [];
    if (includeHidden) {
        return list;
    }
    return list.filter((spec) => !isSpecHidden(spec));
};

/** 조절기·제어기 이미지, 펌웨어, 결선도, 비고 — 항상 맨 아래 */
export const isTrailingFullWidthSpec = (spec) => {
    if (!spec) {
        return false;
    }
    if (spec.fullWidth) {
        return true;
    }
    const title = String(spec.title || '').trim();
    return TRAILING_SPEC_DEFS.adjuster.some((t) => t.title === title)
        || TRAILING_SPEC_DEFS.controller.some((t) => t.title === title);
};

/** 사양 추가로 만든 항목(customTitle)은 이미지·파일·비고 블록 위(기본 사양 옆)에 배치 */
export const orderSpecsCustomBeforeTrailing = (specs = []) => {
    const list = Array.isArray(specs) ? specs : [];
    const head = [];
    const custom = [];
    const trailing = [];
    list.forEach((spec) => {
        if (isTrailingFullWidthSpec(spec)) {
            trailing.push({ ...spec, fullWidth: true });
        } else if (spec?.customTitle) {
            custom.push({ ...spec, fullWidth: false, customTitle: true });
        } else {
            head.push({ ...spec, fullWidth: false });
        }
    });
    return [...head, ...custom, ...trailing];
};

/**
 * 기존 제품 specs를 제품등록 설정(순서·추가 항목·입력방식)에 맞게 정렬.
 * - 설정 순서대로 재배치
 * - 새 사양 항목은 details 빈 값
 * - 기존 제목은 내용 유지, inputType은 설정 반영
 * - 설정에서 제거된 제목은 목록 맨 뒤에 유지(데이터 보존)
 */
export const alignSpecsWithRegistrationConfig = (productType, specs = [], config) => {
    const templates = buildSpecTemplatesFromConfig(productType, config);
    const templateTitles = new Set(templates.map((t) => String(t.title || '').trim()).filter(Boolean));

    const byTitle = new Map();
    for (const spec of Array.isArray(specs) ? specs : []) {
        const title = normalizeWiringDiagramSpecTitle(spec?.title);
        if (!title || byTitle.has(title)) {
            continue;
        }
        byTitle.set(title, {
            ...spec,
            title: normalizeWiringDiagramSpecTitle(spec?.title),
        });
    }

    const aligned = templates.map((template) => {
        const title = String(template.title || '').trim();
        const existing = byTitle.get(title);
        const inputType = template.fullWidth
            ? (template.inputType || 'file')
            : getInputTypeForSpec({ title, inputType: template.inputType }, productType, config);

        if (existing) {
            return {
                ...existing,
                title,
                fullWidth: !!template.fullWidth,
                customTitle: false,
                inputType,
                details: existing.details ?? '',
                hidden: !!existing.hidden,
            };
        }

        return {
            title,
            details: '',
            fullWidth: !!template.fullWidth,
            customTitle: false,
            inputType,
        };
    });

    const orphans = [];
    for (const spec of Array.isArray(specs) ? specs : []) {
        const title = String(spec?.title || '').trim();
        if (!title || templateTitles.has(title)) {
            continue;
        }
        if (isWiringDiagramSpecTitle(title) && templateTitles.has(WIRING_DIAGRAM_SPEC_TITLE)) {
            continue;
        }
        orphans.push({
            ...spec,
            title,
            inputType: getInputTypeForSpec(spec, productType, config),
            customTitle: true,
            fullWidth: false,
        });
    }

    const trailingStart = aligned.findIndex((spec) => isTrailingFullWidthSpec(spec));
    if (trailingStart < 0) {
        return orderSpecsCustomBeforeTrailing([...aligned, ...orphans]);
    }
    return orderSpecsCustomBeforeTrailing([
        ...aligned.slice(0, trailingStart),
        ...orphans,
        ...aligned.slice(trailingStart),
    ]);
};

export const getInputTypeForSpec = (spec, productType, config) => {
    if (spec?.fullWidth || spec?.inputType === 'file') {
        return spec?.inputType || 'file';
    }
    const title = String(spec?.title || '').trim();
    const normalized = normalizeProductRegistrationConfig(config);
    const typeKey = productType === 'controller' ? 'controller' : 'adjuster';
    const found = normalized[typeKey].fields.find((f) => f.title === title);
    if (found) {
        return found.inputType;
    }
    if (spec?.inputType === 'text' || spec?.inputType === 'dropdown' || spec?.inputType === 'date') {
        return spec.inputType;
    }
    return 'dropdown';
};

export const getMinNormalSpecRowsForConfig = (productType, config) => {
    const normalized = normalizeProductRegistrationConfig(config);
    const typeKey = productType === 'controller' ? 'controller' : 'adjuster';
    return Math.max(1, normalized[typeKey].fields.length);
};

export const collectAllTextSpecTitlesFromConfig = (config) => {
    const normalized = normalizeProductRegistrationConfig(config);
    const titles = new Set();
    PRODUCT_TYPES.forEach((typeKey) => {
        normalized[typeKey].fields.forEach((f) => {
            if (f.title) {
                titles.add(f.title);
            }
        });
    });
    return [...titles].sort((a, b) => a.localeCompare(b, 'ko'));
};

/** 제품등록 설정에서 입력 방식이 드롭다운인 사양 항목 제목 (드롭다운 관리 선택 목록용) */
export const collectDropdownSpecTitlesFromConfig = (config) => {
    const normalized = normalizeProductRegistrationConfig(config);
    const titles = new Set();
    PRODUCT_TYPES.forEach((typeKey) => {
        normalized[typeKey].fields.forEach((f) => {
            if (f.title && f.inputType === 'dropdown') {
                titles.add(f.title);
            }
        });
    });
    return [...titles].sort((a, b) => a.localeCompare(b, 'ko'));
};
